import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Folder, ArrowLeft, ArrowUpRight, Lock, Video } from "lucide-react";
import { Button } from "@/components/ui/button";

/* -------------------------------------------------------------------------
   To add a new Drive folder, append to this array.
   • `name`   — display name
   • `note`   — short description shown on the card
   • `link`   — Google Drive folder URL (opens in a new tab)
   • `count`  — optional video count to display
   ------------------------------------------------------------------------- */
const driveFolders = [
  {
    name: "Certification Series",
    note: "Tammy's core certification trainings — watch in order or jump to the module you need.",
    link: "https://drive.google.com/drive/folders/1M4yL-ZJ7LO4Csn1mL4070mV27SaKGv99?usp=sharing",
    count: null,
  },
  {
    name: "Tech & AI Training",
    note: "Practical training on the tools, platforms, and AI workflows Tammy uses in her business.",
    link: "https://drive.google.com/drive/folders/1N0rgx7dzdTn1DLTiBi7qFvyBMRl2JLtj?usp=sharing",
    count: null,
  },
];

export default function VideoLibrary() {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(null); // null until we check

  useEffect(() => {
    const ok = sessionStorage.getItem("portalUnlocked") === "true";
    if (!ok) {
      navigate("/coaching-resources", { replace: true });
      return;
    }
    setUnlocked(true);
  }, [navigate]);

  if (!unlocked) {
    // brief blank while we check / redirect
    return (
      <div className="max-w-md mx-auto px-6 py-32 text-center" data-testid="video-library-checking">
        <Lock className="mx-auto text-foreground/40" size={26} strokeWidth={1.5} />
        <p className="text-foreground/60 mt-4 text-sm">Checking access...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="video-library-page">
      <Link
        to="/coaching-resources"
        className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary transition"
        data-testid="video-library-back"
      >
        <ArrowLeft size={14} /> Back to Agent Portal
      </Link>

      <div className="grid md:grid-cols-12 gap-12 mt-8 mb-16">
        <div className="md:col-span-7">
          <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
            Agent Portal · Members Only
          </span>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.02]">
            Video <span className="italic text-primary">Library.</span>
          </h1>
        </div>
        <p className="md:col-span-4 md:col-start-9 text-lg text-foreground/75 leading-relaxed self-end">
          Curated training folders hosted on Google Drive. Click a folder to
          open it in a new tab — no extra login needed.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {driveFolders.map((f, i) => (
          <a
            key={f.name}
            href={f.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`video-folder-${i}`}
            className="group bg-card border border-border rounded-2xl p-8 card-lift flex flex-col"
          >
            <div className="flex items-start justify-between">
              <div className="w-14 h-14 rounded-2xl bg-primary/12 flex items-center justify-center group-hover:bg-primary/20 transition">
                <Folder className="text-primary" size={26} strokeWidth={1.6} />
              </div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-foreground/55">
                Google Drive
              </span>
            </div>
            <h3 className="font-serif text-2xl mt-6 leading-tight">{f.name}</h3>
            <p className="text-sm text-foreground/70 mt-3 leading-relaxed flex-1">
              {f.note}
            </p>
            <div className="mt-6 pt-5 border-t border-border flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.18em] text-foreground/55 inline-flex items-center gap-1.5">
                <Video size={12} /> {f.count ? `${f.count} videos` : "Open folder"}
              </span>
              <ArrowUpRight
                className="text-primary transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                size={18}
              />
            </div>
          </a>
        ))}
      </div>

      <p className="text-xs text-foreground/55 mt-12 max-w-xl">
        These folders live on Google Drive and inherit Tammy's sharing
        permissions. If you can't open one, message her directly and she'll
        get you access.
      </p>
    </div>
  );
}
